import React from 'react';
import { Typography, Tag, Collapse, Button } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import AuthLayout from '../../layouts/AuthLayout';
import sthenusLogo from '../../assets/sthenus-logo.png';
import squiggleLogo from '../../assets/squiggle.svg';
import supportFaqData from './supportFaqData';
import SupportContactForm from './SupportContactForm';
import '../../styles/support.css';
import '../../styles/delete-account.css';

const { Title, Paragraph, Text } = Typography;

const TOPIC_CHIPS = [
  'Cuenta',
  'Rutinas',
  'Suscripciones',
  'Salud',
  'Privacidad',
  'Soporte',
];

const HERO_PANEL_ITEMS = [
  {
    title: 'Atención',
    description: 'Cuenta, rutinas, suscripciones, salud, privacidad y soporte.',
  },
  {
    title: 'Preguntas frecuentes',
    description: 'Resuelve rápido las dudas más comunes antes de levantar un reporte de soporte.',
  },
  {
    title: 'Privacidad',
    description: 'privacy',
  },
];

const scrollToSection = (id) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

export default function SupportPage() {
  const navigate = useNavigate();

  const mapFaqItems = (items) =>
    items.map((item) => ({
      key: item.key,
      label: item.question,
      children: <Paragraph style={{ margin: 0 }}>{item.answer}</Paragraph>,
    }));

  return (
    <AuthLayout>
      <div className="support-page">
        <div className="delete-account-hero">
          <div className="delete-account-hero__content">
            <span className="delete-account-hero__label">
              <span className="delete-account-hero__label-dot" aria-hidden="true" />
              Centro de ayuda
            </span>
            <Title level={2} className="delete-account-hero__title">
              Soporte Sthenus
            </Title>
            <Paragraph className="delete-account-hero__subtitle">
              Consulta las respuestas frecuentes sobre usuarios, clientes, rutinas, ejercicios
              y reportes. Si necesitas seguimiento, envíanos tu caso desde el formulario.
            </Paragraph>
          </div>

          <aside className="delete-account-hero__panel">
            <div className="delete-account-hero__brand">
              <img
                src={squiggleLogo}
                alt="Sthenus"
                className="delete-account-hero__brand-icon"
              />
              <span className="delete-account-hero__brand-name">Sthenus</span>
            </div>

            <ul className="delete-account-hero__info">
              {HERO_PANEL_ITEMS.map((item) => (
                <li key={item.title} className="delete-account-hero__info-item">
                  <strong>{item.title}</strong>
                  {item.description === 'privacy' ? (
                    <a
                      href="/privacy-policy"
                      className="delete-account-hero__link"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate('/privacy-policy');
                      }}
                    >
                      Ver política de privacidad
                    </a>
                  ) : (
                    item.description
                  )}
                </li>
              ))}
            </ul>
          </aside>
        </div>

        <div className="support-layout">
          <aside className="support-sidebar">
            <img src={sthenusLogo} alt="Sthenus" className="support-sidebar__logo" />

            <ul className="support-sidebar__list">
              <li className="support-sidebar__item">
                <strong>Atención</strong>
                 Cuenta, rutinas, suscripciones, salud, privacidad y soporte.
              </li>
              <li className="support-sidebar__item">
                <strong>Preguntas frecuentes</strong>
                Resuelve rápido las dudas más comunes antes de levantar un reporte de soporte.
              </li>
              <li className="support-sidebar__item">
                <strong>Privacidad</strong>
                <a
                  href="/privacy-policy"
                  className="support-sidebar__link"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/privacy-policy');
                  }}
                >
                  Ver política de privacidad
                </a>
              </li>
            </ul>

            <Button
              type="default"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/login')}
              block
            >
              Regresar al inicio
            </Button>
          </aside>

          <main>
            <div className="support-chips">
              {TOPIC_CHIPS.map((chip) => (
                <Tag key={chip} className="support-chip">
                  {chip}
                </Tag>
              ))}
            </div>

            <nav className="support-nav">
              <a
                href="#faq"
                className="support-nav__link"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('faq');
                }}
              >
                Preguntas frecuentes
              </a>
              <a
                href="#contacto"
                className="support-nav__link"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('contacto');
                }}
              >
                Contactar soporte
              </a>
              <a
                href="/eliminar-datos"
                className="support-nav__link"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/eliminar-datos');
                }}
              >
                Eliminar cuenta
              </a>
              <a
                href="/login"
                className="support-nav__link support-nav__link--muted"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/login');
                }}
              >
                Regresar al inicio
              </a>
              <a
                href="/privacy-policy"
                className="support-nav__link support-nav__link--muted"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/privacy-policy');
                }}
              >
                Política de privacidad
              </a>
              <a
                href="/terms-and-conditions"
                className="support-nav__link support-nav__link--muted"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/terms-and-conditions');
                }}
              >
                Términos y condiciones
              </a>
            </nav>

            <section id="faq" className="support-section support-faq">
              <Title level={3} className="support-section__title">
                Preguntas frecuentes
              </Title>
              <Text className="support-section__description">
                Resuelve rápido las dudas más comunes antes de levantar un reporte de soporte.
              </Text>
              <div className="support-faq__groups">
                {supportFaqData.map((section) => (
                  <div key={section.category} className="support-faq__group">
                    <Title level={4} className="support-faq__category">
                      {section.category}
                    </Title>
                    <Collapse
                      items={mapFaqItems(section.items)}
                      bordered={false}
                      expandIconPosition="end"
                    />
                  </div>
                ))}
              </div>
            </section>

            <section id="contacto" className="support-section">
              <Title level={3} className="support-section__title">
                Contactar soporte
              </Title>
              <Text className="support-section__description">
                Comparte tus datos y describe tu consulta. Usa el mismo correo con el que te
                registraste en la aplicación para facilitar el seguimiento.
              </Text>
              <SupportContactForm />
            </section>

            <footer className="support-footer">
              <div className="support-footer__links">
                <a
                  href="/login"
                  className="support-footer__link"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/login');
                  }}
                >
                  Regresar al inicio
                </a>
                <a
                  href="/privacy-policy"
                  className="support-footer__link"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/privacy-policy');
                  }}
                >
                  Política de privacidad
                </a>
                <a
                  href="/terms-and-conditions"
                  className="support-footer__link"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/terms-and-conditions');
                  }}
                >
                  Términos y condiciones
                </a>
                <a
                  href="/eliminar-datos"
                  className="support-footer__link"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/eliminar-datos');
                  }}
                >
                  Eliminar cuenta
                </a>
              </div>
              <Text type="secondary">
                Sthenus | Centro de ayuda para el panel de administración.
              </Text>
            </footer>
          </main>
        </div>
      </div>
    </AuthLayout>
  );
}
